import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPaperclip, faSpinner, faFaceSmile } from '@fortawesome/free-solid-svg-icons';
import NotificationModal from '../NotificationModal';
import '../../../styling/ChatRoom.css';
import { useApiClient } from '../../../service/API';
import DisplayMessage from './DisplayMessage';
import EmojiPicker from 'emoji-picker-react';
import { createPortal } from 'react-dom';
import { useWebSocketContext } from '../../WebSocket/WebSocketContext';
import { getApiErrorMessage } from '../../../service/errorHandler';

// --- Allowed types (MIME + extension) ---
const ALLOWED_MIMES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'message/rfc822',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const ALLOWED_EXTS = new Set([
  '.pdf', '.jpg', '.jpeg', '.png', '.gif',
  '.xls', '.xlsx', '.eml',
  '.doc', '.docx',
]);

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

function getExt(name = '') {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

function isAllowedFile(file) {
  if (!file) return false;
  const { type, name } = file;
  const ext = getExt(name);
  if (ALLOWED_MIMES.has(type)) return true;
  if (ALLOWED_EXTS.has(ext)) return true;
  return false;
}

// ---------- timestamp/shape normalization ----------
const sanitizeTimestamp = (v) => {
  if (!v) return null;
  let s = String(v).trim();
  const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(s);
  if (!hasTZ) s += 'Z';
  s = s.replace(/(\.\d{3})\d+/, '$1');
  let ms = Date.parse(s);
  if (isNaN(ms)) {
    s = s.replace(/\.\d+/, '');
    ms = Date.parse(s);
  }
  return isNaN(ms) ? null : new Date(ms).toISOString();
};

const toMs = (v) => {
  if (!v) return NaN;
  let ms = Date.parse(v);
  if (!isNaN(ms)) return ms;
  const s = sanitizeTimestamp(v);
  if (s) {
    ms = Date.parse(s);
    if (!isNaN(ms)) return ms;
  }
  return NaN;
};

const coerceCreatedAt = (m) =>
  m?.createdAt || m?.createdDate || m?.created_at || m?.created_on || null;

const normalizeMsg = (m) => {
  const rawTs = coerceCreatedAt(m);
  const safeIso = sanitizeTimestamp(rawTs) || sanitizeTimestamp(new Date().toISOString());
  return {
    ...m,
    createdAt: safeIso,
    pending: Boolean(m?.pending),
  };
};

const isClientTemp = (m) =>
  typeof m?.clientMessageId === 'string' && m.clientMessageId.startsWith('client-');

const normalizeComparableContent = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/&nbsp;/gi, ' ')
    .replace(/<div><br><\/div>/gi, '\n')
    .replace(/<div>/gi, '\n')
    .replace(/<\/div>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\u200B/g, '')
    .replace(/\s+\n/g, '\n')
    .replace(/\n\s+/g, '\n')
    .replace(/\n+/g, '\n')
    .trim();
};

const insertSorted = (list, rawMsg) => {
  const msg = normalizeMsg(rawMsg);
  const ms = toMs(msg.createdAt);
  const next = list.slice();

  let i = next.length - 1;
  while (i >= 0 && toMs(next[i]?.createdAt) > ms) {
    i -= 1;
  }

  next.splice(i + 1, 0, msg);
  return next;
};

const Chat = ({ ticketId, senderEmail, receiverEmail, isChatDisabled }) => {
  const apiClient = useApiClient();
  const apiRef = useRef(apiClient);
  useEffect(() => { apiRef.current = apiClient; }, [apiClient]);

  const [messages, setMessages] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [isFileUploading, setFileUploading] = useState(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef(null);
  const [pickerStyle, setPickerStyle] = useState({ top: 0, left: 0 });

  const historyPositionRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const messageDisplayRef = useRef(null);
  const textAreaRef = useRef(null);
  const savedRangeRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const resyncTimeoutRef = useRef(null);

  const caretMarkerIdRef = useRef(null);
  const CARET_ATTR = 'data-caret-marker';

  const createCaretMarker = () => {
    const editor = textAreaRef.current;
    if (!editor) return null;

    const id = `cm-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const sel = window.getSelection?.();
    let range;
    if (sel?.rangeCount && editor.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0).cloneRange();
    } else {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    const span = document.createElement('span');
    span.setAttribute(CARET_ATTR, id);
    span.setAttribute('contenteditable', 'false');
    span.style.display = 'inline';
    span.style.width = '0';
    span.style.height = '0';
    span.style.lineHeight = '0';
    span.style.overflow = 'hidden';
    span.appendChild(document.createTextNode('\u200B'));

    range.collapse(true);
    range.insertNode(span);

    const after = document.createRange();
    after.setStartAfter(span);
    after.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(after);

    caretMarkerIdRef.current = id;
    return id;
  };

  const removeCaretMarker = () => {
    const editor = textAreaRef.current;
    const id = caretMarkerIdRef.current;
    if (!editor || !id) return;
    const node = editor.querySelector(`[${CARET_ATTR}="${id}"]`);
    if (node) node.remove();
    caretMarkerIdRef.current = null;
  };

  const insertAtCaretMarker = (text) => {
    const editor = textAreaRef.current;
    const id = caretMarkerIdRef.current;
    if (!editor || !id) return;

    const node = editor.querySelector(`[${CARET_ATTR}="${id}"]`);
    if (!node) return;

    const emojiNode = document.createTextNode(text);
    node.parentNode.insertBefore(emojiNode, node);

    const sel = window.getSelection?.();
    const range = document.createRange();
    range.setStartAfter(emojiNode);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);

    node.remove();
    caretMarkerIdRef.current = null;
    editor.focus();
  };

  const placeCaretAfterNode = (node) => {
    const sel = window.getSelection?.();
    if (!sel) return;

    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const lastFetchRef = useRef(0);
  const fetchSeqRef = useRef(0);
  const MIN_FETCH_INTERVAL = 20000;

  const {
    isConnected,
    stompClient,
    ticketEvents,
    readEvents,
  } = useWebSocketContext();

  function setCaretByCharOffset(root, offset) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node;
    let remaining = offset;

    while ((node = walker.nextNode())) {
      const len = node.nodeValue.length;
      if (remaining <= len) {
        const sel = window.getSelection();
        const range = document.createRange();
        range.setStart(node, Math.max(0, remaining));
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        return range.cloneRange();
      }
      remaining -= len;
    }

    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    return range.cloneRange();
  }

  const positionPicker = useCallback(() => {
    const btn = emojiButtonRef.current;
    if (!btn) return;

    const PICKER_W = 340;
    const PICKER_H = 360;
    const GAP = 8;
    const PADDING = 8;
    const rect = btn.getBoundingClientRect();

    let top = rect.top - PICKER_H - GAP;
    if (top < PADDING) {
      top = rect.bottom + GAP;
    }

    let left = rect.right - PICKER_W;
    const maxLeft = window.innerWidth - PICKER_W - PADDING;
    left = Math.max(PADDING, Math.min(left, maxLeft));

    setPickerStyle({ top, left });
  }, []);

  useEffect(() => {
    const saveIfInside = () => {
      const editor = textAreaRef.current;
      if (!editor) return;
      const sel = window.getSelection?.();
      if (sel?.rangeCount && editor.contains(sel.anchorNode)) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener('selectionchange', saveIfInside);
    return () => document.removeEventListener('selectionchange', saveIfInside);
  }, []);

  const handleEmojiButtonMouseDown = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    const editor = textAreaRef.current;
    if (!editor) return;

    editor.focus();
    createCaretMarker();

    setShowEmojiPicker(prev => !prev);
    requestAnimationFrame(positionPicker);
  };

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = () => positionPicker();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [showEmojiPicker, positionPicker]);

  const getFileId = (m) =>
    m?.fileId ?? m?.filedata?.fileId ?? m?.filediscussionId ?? null;

  const hasFile = (m) => Boolean(getFileId(m));

  const makeIdentity = (m) => {
    if (m?.msgId) return `srv:${m.msgId}`;
    if (m?.clientMessageId) return `cli:${m.clientMessageId}`;
    const fid = getFileId(m);
    if (fid) return `file:${fid}`;
    return `local:${m.createdBy || ''}:${m.createdAt || ''}`;
  };

  const makeDedupeKey = (m, currentTicketId) => {
    const kind = hasFile(m) ? 'file' : 'text';
    return `${m?.createdBy || ''}|${currentTicketId || ''}|${kind}|${makeIdentity(m)}`;
  };

  const dedupeList = (list, currentTicketId) => {
    const byKey = new Map();

    for (const raw of list) {
      const m = normalizeMsg(raw);
      const key = makeDedupeKey(m, currentTicketId);
      const prev = byKey.get(key);

      if (!prev) {
        byKey.set(key, m);
        continue;
      }

      const prevIsBetter =
        (!prev.pending && m.pending) ||
        (prev.pending === m.pending && toMs(prev.createdAt) >= toMs(m.createdAt));

      byKey.set(key, prevIsBetter ? prev : m);
    }

    return Array.from(byKey.values()).sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
  };

  const colonsToUnicode = async (text) => {
    if (!text) return text;
    const map = new Map([
      [':)', '😊'], [':-)', '😊'],
      [':D', '😄'], [':-D', '😄'],
      [':(', '☹️'], [':-(', '☹️'],
      [':P', '😛'], [':-P', '😛'],
      [';)', '😉'], [';-)', '😉'],
    ]);
    let out = text;
    for (const [k, v] of map) out = out.split(k).join(v);
    return out;
  };

  const moveCaretMarkerToCurrentSelection = () => {
    const editor = textAreaRef.current;
    if (!editor) return;
    const sel = window.getSelection?.();
    if (!sel?.rangeCount || !editor.contains(sel.anchorNode)) return;

    removeCaretMarker();
    createCaretMarker();
  };

  useEffect(() => {
    if (!showEmojiPicker) return;
    const editor = textAreaRef.current;
    if (!editor) return;

    const onMouseUp = () => requestAnimationFrame(moveCaretMarkerToCurrentSelection);
    const onKeyUp = () => requestAnimationFrame(moveCaretMarkerToCurrentSelection);

    editor.addEventListener('mouseup', onMouseUp);
    editor.addEventListener('keyup', onKeyUp);

    const onSelectionChange = () => {
      const sel = window.getSelection?.();
      if (sel?.rangeCount && editor.contains(sel.anchorNode)) {
        requestAnimationFrame(moveCaretMarkerToCurrentSelection);
      }
    };

    document.addEventListener('selectionchange', onSelectionChange, true);

    return () => {
      editor.removeEventListener('mouseup', onMouseUp);
      editor.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('selectionchange', onSelectionChange, true);
      removeCaretMarker();
    };
  }, [showEmojiPicker]);

  const isFileAlreadyUploaded = (file) => {
    if (!files || !Array.isArray(files)) return false;
    const uploadedNames = new Set(files.map(f => f.name));
    if (!uploadedNames.has(file.name)) return false;

    let baseName = file.name;
    let extension = '';
    const dotIndex = file.name.lastIndexOf('.');
    if (dotIndex !== -1) {
      baseName = file.name.slice(0, dotIndex);
      extension = file.name.slice(dotIndex);
    }

    let counter = 1;
    let newFileName = `${baseName} (${counter})${extension}`;
    while (uploadedNames.has(newFileName)) {
      counter++;
      newFileName = `${baseName} (${counter})${extension}`;
      if (counter > 1000) break;
    }

    return newFileName;
  };

  const stableTicketIdRef = useRef(null);
  useEffect(() => {
    if (ticketId) stableTicketIdRef.current = ticketId;
  }, [ticketId]);

  const getCurrentTicketId = () => ticketId || stableTicketIdRef.current;

  const sameKindAndContent = (a, b) => {
    const aFile = getFileId(a);
    const bFile = getFileId(b);

    if (aFile || bFile) {
      return aFile && bFile && String(aFile) === String(bFile);
    }

    return normalizeComparableContent(a?.content) === normalizeComparableContent(b?.content);
  };

  const findFallbackReplaceIndex = (prev, incoming) => {
    return prev.findIndex((raw) => {
      const m = normalizeMsg(raw);

      if (!isClientTemp(m)) return false;
      if (!(m.pending || m.sendError)) return false;
      if (String(m.createdBy || '') !== String(incoming.createdBy || '')) return false;

      const dt = Math.abs(toMs(m.createdAt) - toMs(incoming.createdAt));
      if (!Number.isNaN(dt) && dt > 2 * 60 * 1000) return false;

      return sameKindAndContent(m, incoming);
    });
  };

  const mergeServerIntoLocal = useCallback((prev, serverList, currentTicketId) => {
    const next = prev.map(normalizeMsg);

    for (const raw of serverList) {
      const s = normalizeMsg(raw);

      if (s.msgId) {
        const idx = next.findIndex(p => p.msgId === s.msgId);
        if (idx !== -1) {
          next[idx] = { ...s, pending: false, sendError: false };
          continue;
        }
      }

      if (s.clientMessageId) {
        const idx = next.findIndex(p => p.clientMessageId === s.clientMessageId);
        if (idx !== -1) {
          next[idx] = { ...s, pending: false, sendError: false };
          continue;
        }
      }

      const fb = findFallbackReplaceIndex(next, s);
      if (fb !== -1) {
        next[fb] = { ...s, pending: false, sendError: false };
        continue;
      }

      next.push(s);
    }

    return dedupeList(next, currentTicketId);
  }, []);

  const handleNewMessage = useCallback((msg) => {
    if (msg?.type === 'REFRESH') return;

    const currentTicketId = getCurrentTicketId();
    const msgTid = msg?.ticketId != null ? String(msg.ticketId) : null;
    if (!msgTid || msgTid !== String(currentTicketId)) return;

    const incoming = normalizeMsg({ ...msg, pending: false });

    setMessages((prev) => {
      if (incoming.clientMessageId) {
        const idx = prev.findIndex(
          raw => normalizeMsg(raw).clientMessageId === incoming.clientMessageId
        );
        if (idx !== -1) {
          const copy = prev.slice();
          copy[idx] = { ...incoming, pending: false, sendError: false };
          return dedupeList(copy, currentTicketId);
        }
      }

      if (incoming.msgId) {
        const idx = prev.findIndex(raw => normalizeMsg(raw).msgId === incoming.msgId);
        if (idx !== -1) {
          const copy = prev.slice();
          copy[idx] = { ...incoming, pending: false, sendError: false };
          return dedupeList(copy, currentTicketId);
        }
      }

      const fb = findFallbackReplaceIndex(prev, incoming);
      if (fb !== -1) {
        const copy = prev.slice();
        copy[fb] = { ...incoming, pending: false, sendError: false };
        return dedupeList(copy, currentTicketId);
      }

      return dedupeList(insertSorted(prev, incoming), currentTicketId);
    });

    if (msg.createdBy !== 'System' && msg.createdBy !== senderEmail && msg.msgId) {
      apiRef.current.put(`/tickets/chat/update-read-status/${msg.msgId}`).catch(() => {});
    }
  }, [senderEmail]);

  const updateMessageReadStatus = useCallback((message) => {
    const currentTicketId = getCurrentTicketId();
    if (!message?.ticketId || String(message.ticketId) !== String(currentTicketId)) return;

    setMessages(prev =>
      prev.map(raw => {
        const m = normalizeMsg(raw);
        return (m.msgId === message.msgId && String(m.ticketId) === String(currentTicketId))
          ? { ...m, messageRead: message.messageRead }
          : m;
      })
    );
  }, []);

  const lastProcessedRef = useRef(0);
  const lastReadProcessedRef = useRef(0);

  useEffect(() => {
    lastProcessedRef.current = 0;
    lastReadProcessedRef.current = 0;
  }, [ticketId]);

  useEffect(() => {
    if (!ticketEvents?.length) return;

    const newEvents = ticketEvents.slice(lastProcessedRef.current);

    newEvents.forEach(evt => {
      handleNewMessage(evt);

      if (evt?.type === 'REFRESH') {
        if (resyncTimeoutRef.current) clearTimeout(resyncTimeoutRef.current);
        resyncTimeoutRef.current = setTimeout(() => {
          const currentTicketId = getCurrentTicketId();
          if (currentTicketId) {
            fetchHistoryMessageData(currentTicketId);
          }
        }, 600);
      }
    });

    lastProcessedRef.current = ticketEvents.length;
  }, [ticketEvents, handleNewMessage]);

  useEffect(() => {
    if (!readEvents?.length) return;

    const newEvents = readEvents.slice(lastReadProcessedRef.current);
    newEvents.forEach(evt => updateMessageReadStatus(evt));
    lastReadProcessedRef.current = readEvents.length;
  }, [readEvents, updateMessageReadStatus]);

  useEffect(() => {
    const T_FAIL_MS = 30000;

    const tick = () => {
      let changed = false;
      const now = Date.now();

      const compute = (prev) => prev.map((raw) => {
        const m = normalizeMsg(raw);
        if (isClientTemp(m) && m.pending && !m.sendError) {
          const created = toMs(m.createdAt);
          if (!Number.isNaN(created) && (now - created) > T_FAIL_MS) {
            changed = true;
            return { ...m, pending: true, sendError: true };
          }
        }
        return m;
      });

      setMessages(prev => {
        const next = compute(prev);
        return changed ? next : prev;
      });
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const fetchHistoryMessageData = useCallback(async (tid) => {
    const currentTicketId = tid || ticketId;
    if (!currentTicketId) return;

    const seq = ++fetchSeqRef.current;

    try {
      const res = await apiRef.current.get(`/tickets/chat/${currentTicketId}`);

      if (seq !== fetchSeqRef.current) return;

      const data = Array.isArray(res.data) ? res.data : [];
      const idStr = String(currentTicketId);

      const normalized = data.map(raw =>
        normalizeMsg({
          ...raw,
          ticketId: raw.ticketId != null ? raw.ticketId : idStr,
          pending: false,
          sendError: false,
        })
      );

      setMessages(prev => {
        const otherTickets = prev.filter(
          (m) => String(m.ticketId) !== String(currentTicketId)
        );

        const currentTicketLocal = prev.filter(
          (m) => String(m.ticketId) === String(currentTicketId)
        );

        const mergedCurrentTicket = mergeServerIntoLocal(
          currentTicketLocal,
          normalized,
          currentTicketId
        );

        return [...otherTickets, ...mergedCurrentTicket];
      });

      const fileList = normalized
        .filter(d => d.filedata?.name)
        .map(d => d.filedata);

      setFiles(fileList);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      const msg = getApiErrorMessage(error, "Something went wrong.");
      setNotificationMessage(msg);
      setShowNotification(true);
    }
  }, [ticketId, mergeServerIntoLocal]);

  useEffect(() => {
    if (isConnected && ticketId) {
      fetchHistoryMessageData(ticketId);
    }
  }, [isConnected, ticketId, fetchHistoryMessageData]);

  useEffect(() => {
    setMessages([]);
    setFiles([]);
    lastFetchRef.current = 0;
    fetchSeqRef.current += 1;
    lastProcessedRef.current = 0;
    lastReadProcessedRef.current = 0;

    if (textAreaRef.current) {
      textAreaRef.current.innerHTML = '';
    }

    if (ticketId) {
      fetchHistoryMessageData(ticketId);
    }
  }, [ticketId, fetchHistoryMessageData]);

  useEffect(() => {
    const sync = () => {
      const now = Date.now();
      if (
        document.visibilityState === 'visible' &&
        now - lastFetchRef.current > MIN_FETCH_INTERVAL
      ) {
        lastFetchRef.current = now;
        if (ticketId) fetchHistoryMessageData(ticketId);
      }
    };

    window.addEventListener('focus', sync);
    window.addEventListener('online', sync);

    const onVis = () => {
      if (document.visibilityState === 'visible') sync();
    };

    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener('online', sync);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ticketId, fetchHistoryMessageData]);

  const getComposerHTML = () => (textAreaRef.current?.innerHTML ?? '');

  const getComposerText = () => {
    const el = textAreaRef.current;
    if (!el) return '';
    const html = (el.innerHTML || '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/div>\s*<div>/gi, '\n');
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || '').trim();
  };

  const isSanitizingRef = useRef(false);

  const clearComposer = () => {
    if (!textAreaRef.current) return;
    isSanitizingRef.current = true;
    textAreaRef.current.innerHTML = '';
    requestAnimationFrame(() => { isSanitizingRef.current = false; });
  };

  const scrollToBottom = () => {
    if (messageDisplayRef.current) {
      messageDisplayRef.current.scrollTop = messageDisplayRef.current.scrollHeight;
    }
  };

  const sendMessage = async (rawContent, filediscussionId = null) => {
    const currentTicketId = getCurrentTicketId();
    const html = typeof rawContent === 'string' ? rawContent : getComposerHTML();
    const plain = getComposerText();

    if ((plain === '' || plain == null) && filediscussionId == null) return;

    if (!currentTicketId) {
      setNotificationMessage('Cannot send message: ticket is not ready yet.');
      setShowNotification(true);
      return;
    }

    if (!isConnected) {
      setNotificationMessage('Cannot send message, WebSocket is not connected.');
      setShowNotification(true);
      return;
    }

    const clientMessageId = `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const pendingMsg = normalizeMsg({
      clientMessageId,
      ticketId: currentTicketId,
      createdBy: senderEmail,
      content: html,
      createdAt: new Date().toISOString(),
      pending: true,
      fileId: filediscussionId ?? null,
      filedata: filediscussionId ? { fileId: filediscussionId, name: html } : undefined,
      dedupeHint: filediscussionId ? { fileId: filediscussionId, content: html } : undefined,
    });

    setMessages(prev => dedupeList(insertSorted(prev, pendingMsg), currentTicketId));
    clearComposer();
    scrollToBottom();

    const outbound = {
      senderEmail,
      receiverEmail,
      ticketId: currentTicketId,
      content: html,
      fileId: filediscussionId,
      clientMessageId,
      dedupeHint: pendingMsg.dedupeHint,
    };

    try {
      if (!stompClient) throw new Error('No STOMP client available');
      stompClient.send(`/app/ticket/${currentTicketId}`, {}, JSON.stringify(outbound));
    } catch (err) {
      setMessages(prev =>
        prev.map(m =>
          (m.clientMessageId === clientMessageId)
            ? { ...m, pending: false, sendError: true }
            : m
        )
      );
      setNotificationMessage('Failed to send message. Please try again.');
      setShowNotification(true);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const showTicketClosedMessage = useCallback(() => {
    setNotificationMessage(
      'This ticket has been closed. You cannot send messages or upload files.'
    );
    setShowNotification(true);
  }, []);

  const uploadHandler = async (event) => {
    event.preventDefault();
    if (isChatDisabled) {
      showTicketClosedMessage();
      return;
    }

    setFileUploading(true);

    const file = event.target.files?.[0];
    if (!file) {
      setFileUploading(false);
      return;
    }

    try {
      const versionedFileName = isFileAlreadyUploaded(file);
      const fileToUpload = versionedFileName
        ? new File([file], versionedFileName, { type: file.type })
        : file;

      if (!isAllowedFile(fileToUpload)) {
        setNotificationMessage('Invalid file type. Please upload a valid file (.doc, .docx, .pdf, images, etc).');
        setShowNotification(true);
        setFileUploading(false);
        return;
      }

      if (fileToUpload.size > MAX_SIZE) {
        setNotificationMessage('File size exceeds the maximum allowed size of 10 MB.');
        setShowNotification(true);
        setFileUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('ticketId', getCurrentTicketId() || ticketId);
      formData.append('filename', fileToUpload);

      const response = await apiRef.current.post('/files/upload-chatfiles', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.status === 200 && response.data && response.data.fileId) {
        const uploadedFileData = response.data;
        setFiles(prev => [...prev, uploadedFileData]);

        await sendMessage(`${fileToUpload.name}`, uploadedFileData.fileId);

        setNotificationMessage('File uploaded and message sent successfully!');
        setShowNotification(true);
      } else {
        setNotificationMessage('Upload succeeded but failed to send message.');
        setShowNotification(true);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setNotificationMessage('Error uploading file. Please try again.');
      setShowNotification(true);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = null;
      setFileUploading(false);
    }
  };

  const uploadPastedFile = async (file) => {
    setFileUploading(true);

    if (!isAllowedFile(file)) {
      setNotificationMessage('Invalid pasted file type. Allowed: .doc, .docx, .pdf, images, etc.');
      setShowNotification(true);
      setFileUploading(false);
      return;
    }

    if (file.size > MAX_SIZE) {
      setNotificationMessage('Pasted file exceeds the maximum allowed size of 10 MB.');
      setShowNotification(true);
      setFileUploading(false);
      return;
    }

    try {
      const versionedName = isFileAlreadyUploaded(file);
      const fileToUpload = versionedName
        ? new File([file], versionedName, { type: file.type })
        : file;

      const formData = new FormData();
      formData.append('ticketId', getCurrentTicketId() || ticketId);
      formData.append('filename', fileToUpload);

      const res = await apiRef.current.post('/files/upload-chatfiles', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.status === 200 && res.data?.fileId) {
        const uploaded = res.data;
        setFiles(prev => [...prev, uploaded]);
        await sendMessage(`${fileToUpload.name}`, uploaded.fileId);
        setNotificationMessage('Image pasted and sent successfully!');
        setShowNotification(true);
      } else {
        setNotificationMessage('Upload succeeded but failed to send message.');
        setShowNotification(true);
      }
    } catch (err) {
      console.error('Paste upload error:', err);
      setNotificationMessage('Error uploading pasted image. Please try again.');
      setShowNotification(true);
    } finally {
      setFileUploading(false);
    }
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    let d = new Date(dateString.endsWith('Z') ? dateString : `${dateString}Z`);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }, []);

  const handleKeyDown = (e) => {
    if (!(!isFileUploading && !isChatDisabled && isConnected)) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(getComposerHTML());
      clearComposer();
      scrollToBottom();
      return;
    }

    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();

      const editor = textAreaRef.current;
      const sel = window.getSelection?.();

      if (!editor || !sel?.rangeCount || !editor.contains(sel.anchorNode)) {
        return;
      }

      const range = sel.getRangeAt(0);
      range.deleteContents();

      const br = document.createElement('br');
      range.insertNode(br);

      if (!br.nextSibling && br.parentNode === editor) {
        const br2 = document.createElement('br');
        br.parentNode.insertBefore(br2, br.nextSibling);
      }

      placeCaretAfterNode(br);
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const handleInputChange = async () => {
    const editor = textAreaRef.current;
    if (!editor) return;
    if (isSanitizingRef.current) return;

    const beforeSelOffset = (() => {
      const sel = window.getSelection();
      if (!sel?.rangeCount || !editor.contains(sel.anchorNode)) return null;
      const r = sel.getRangeAt(0);
      const pre = r.cloneRange();
      pre.selectNodeContents(editor);
      pre.setEnd(r.endContainer, r.endOffset);
      return pre.toString().length;
    })();

    const htmlBefore = editor.innerHTML;
    const htmlAfter = await colonsToUnicode(htmlBefore);

    if (htmlBefore === htmlAfter) {
      const sel = window.getSelection();
      if (sel?.rangeCount && editor.contains(sel.anchorNode)) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      }
      return;
    }

    isSanitizingRef.current = true;
    editor.innerHTML = htmlAfter;
    requestAnimationFrame(() => {
      editor.focus();
      if (beforeSelOffset != null) {
        const newRange = setCaretByCharOffset(editor, beforeSelOffset);
        savedRangeRef.current = newRange;
      }
      isSanitizingRef.current = false;
    });
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection?.rangeCount) return;
      const range = selection.getRangeAt(0);
      const cursorOffset = range.startOffset;
      historyPositionRef.current = cursorPositionRef.current;
      cursorPositionRef.current = cursorOffset;
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const handlePaste = async (e) => {
    if (isChatDisabled) {
      e.preventDefault();
      showTicketClosedMessage();
      return;
    }

    const dt = e.clipboardData;
    if (!dt) return;

    let filesList = Array.from(dt.files || []);
    if (filesList.length === 0 && dt.items) {
      filesList = Array.from(dt.items)
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter(Boolean);
    }

    const imageFiles = filesList.filter(f => f && f.type && f.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      e.preventDefault();

      const timestamp = new Date();
      const niceStamp = timestamp.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      let index = 1;

      for (const img of imageFiles) {
        const ext = img.type === 'image/png' ? '.png'
          : img.type === 'image/jpeg' ? '.jpg'
          : img.type === 'image/gif' ? '.gif'
          : '';
        const defaultName = `Screenshot ${niceStamp}${imageFiles.length > 1 ? ` (${index})` : ''}${ext}`;
        const fileWithName = img.name ? img : new File([img], defaultName, { type: img.type });
        await uploadPastedFile(fileWithName);
        index += 1;
      }

      if (textAreaRef.current) {
        textAreaRef.current.innerHTML = '';
      }
      return;
    }

    setTimeout(() => {
      const tag = textAreaRef.current;
      if (!tag) return;
      const sel = window.getSelection();
      const range = document.createRange();
      const last = tag.lastChild;
      const len = last?.nodeType === Node.TEXT_NODE ? last.length : (last?.childNodes?.length || 0);
      if (last && sel) {
        try {
          range.setStart(last, len);
          range.setEnd(last, len);
          sel.removeAllRanges();
          sel.addRange(range);
        } catch {}
      }
      tag.focus();
    }, 0);
  };

  const handleEmojiSelect = (emoji) => {
    const glyph = emoji || '';
    if (!glyph) return;

    if (caretMarkerIdRef.current) {
      insertAtCaretMarker(glyph);
    } else {
      const editor = textAreaRef.current;
      const sel = window.getSelection?.();
      if (editor && sel?.rangeCount && editor.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        const node = document.createTextNode(glyph);
        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        editor.focus();
      }
    }

    setShowEmojiPicker(false);
  };

  return (
    <div className="chat-room">
      <h2>Chat Room</h2>

      <div className="message-display" ref={messageDisplayRef}>
        {(!messages || messages.length === 0) ? (
          <p>No messages yet.</p>
        ) : (
          messages
            .filter(msg => !ticketId || String(msg.ticketId) === String(ticketId))
            .map((msg) => (
              <div
                className={
                  `message ${msg.createdBy === senderEmail ? 'sender' : 'receiver'}`
                  + (msg.pending ? ' pending' : '')
                  + (msg.sendError ? ' send-error' : '')
                }
                key={`${msg.msgId || msg.clientMessageId || `${msg.createdBy}-${msg.createdAt}`}`}
              >
                <DisplayMessage msg={msg} formatDate={formatDate} apiClient={apiRef.current} />
              </div>
            ))
        )}
      </div>

      <div className="input-area">
        <FontAwesomeIcon
          icon={faPaperclip}
          className={isChatDisabled ? 'chat-icon-disabled' : ''}
          onClick={() => {
            if (isChatDisabled) {
              showTicketClosedMessage();
              return;
            }

            if (!isFileUploading) {
              fileInputRef.current?.click();
            }
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.eml,.doc,.docx"
          onChange={uploadHandler}
        />

        <div
          className="form-control chat-input-area"
          onClick={() => {
            if (isChatDisabled) {
              showTicketClosedMessage();
            }
          }}
        >
          <div
            spellCheck="true"
            className="message-input area"
            tabIndex={0}
            contentEditable={!isFileUploading && !isChatDisabled && isConnected}
            ref={textAreaRef}
            onInput={handleInputChange}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            disabled={!isConnected || !ticketId || isFileUploading || isChatDisabled}
          />
          <FontAwesomeIcon
            className={`icon ${isChatDisabled ? 'chat-icon-disabled' : ''}`}
            icon={faFaceSmile}
            onMouseDown={(e) => {
              if (isChatDisabled) {
                e.preventDefault();
                showTicketClosedMessage();
                return;
              }

              if (isConnected) {
                handleEmojiButtonMouseDown(e);
              }
            }}
            ref={emojiButtonRef}
          />
        </div>

        {showEmojiPicker && createPortal(
          <div
            className="emoji-picker-floating"
            ref={emojiPickerRef}
            style={pickerStyle}
            onMouseDownCapture={(e) => e.preventDefault()}
            onClickCapture={(e) => e.preventDefault()}
          >
            <EmojiPicker
              onEmojiClick={(emoji) => handleEmojiSelect(emoji.emoji)}
              searchDisabled={true}
              skinTonesDisabled={false}
              previewConfig={{ showPreview: false }}
              width={340}
              height={360}
            />
          </div>,
          document.body
        )}

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => sendMessage(getComposerHTML())}
          disabled={!isConnected || !getCurrentTicketId() || isFileUploading || isChatDisabled}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {isFileUploading && isConnected ? (
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '5px' }} />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '5px' }} />
          )}
          Send
        </button>
      </div>

      <NotificationModal
        show={showNotification}
        message={notificationMessage}
        onClose={() => setShowNotification(false)}
      />
    </div>
  );
};

export default Chat;